import useFormStyles from '../../styles/useFormStyles';
import { useForm, SubmitHandler, FormProvider, NestedValue } from 'react-hook-form';
import { Grid, Button } from '@mui/material';
import {
  TextField,
  CalendarDatePicker,
  RadioButtonGroup,
  RadioButton,
  SelectionBox,
} from './../../components/form/react-hook-form-components';
import { ComboBoxOption } from '../../components/form/controls/ComboBox';
import { ClientDto, PriorityItemDto, ProjectDto } from '../../api';
import ComboBox from './../../components/form/controls/ComboBox';
import { useState } from 'react';
import { getComboBoxOptions, getComboBoxOption, convertToClientDto } from '../../utils/Utils';
import CreateClientPopup from '../../components/form/popups/CreateClientPopup';
import CreateProjectPopup from '../../components/form/popups/CreateProjectPopup';
import useEmployees from '../../hooks/useEmployees';
import useClients from '../../hooks/useClients';
import { useQuery } from 'react-query';
import { Employee } from '../../models/Employee';
import { Status } from '../../components/datagrid/ag-grid/renderStatus';
import PriorityHistory from './PriorityHistory';

interface IPriorityFormProps {
  onSubmit: (values: PriorityItemDto) => void;
  onCancel: () => void;
  employee?: Employee;
  order: number;
  priority?: PriorityItemDto;
  showEmployeeDropdown?: boolean;
}

export interface IPriorityFormFields {
  id: number;
  employee: NestedValue<ComboBoxOption>;
  projectId: number;
  order: number;
  status: string;
  utilisation: number;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
}

const getEmployeeComboBoxOption = (employee: Employee) => {
  const option = {
    label: `${employee.firstName} ${employee.lastName}`,
    value: employee.id,
  } as ComboBoxOption;

  return option;
};

function PriorityForm(props: IPriorityFormProps) {
  const { onCancel, onSubmit, employee, order, priority, showEmployeeDropdown = false } = props;
  const classes = useFormStyles();
  const { data: employees } = useEmployees();
  const { data: clients, isLoading: isSuccess } = useClients();
  const methods = useForm<IPriorityFormFields>({
    defaultValues: {
      id: priority?.id,
      employee: employee && getEmployeeComboBoxOption(employee),
      order: order,
      status: priority?.status ?? 'Request',
      utilisation: priority?.utilisation,
      description: priority?.description ?? '',
      startDate: priority ? new Date(priority.startDate) : null,
      endDate: priority ? new Date(priority.endDate) : null,
    },
  });
  const { handleSubmit, watch } = methods;
  const [client, setClient] = useState<ComboBoxOption | undefined>(getInitialClientComboBoxOption);
  const [clientError, setClientError] = useState('');
  const [project, setProject] = useState<ComboBoxOption | undefined>(getInitialProjectComboBoxOption);
  const [projectError, setProjectError] = useState('');
  const [isCreateClientFormOpen, setIsCreateClientFormOpen] = useState(false);
  const [isCreateProjectFormOpen, setIsCreateProjectFormOpen] = useState(false);
  const [clientNameSuggestion, setClientNameSuggestion] = useState('');
  const [projectNameSuggestion, setProjectNameSuggestion] = useState('');

  useQuery(`${isSuccess}`, () => {
    if (!client?.value) {
      setClient(getInitialClientComboBoxOption);
    }
  });

  /**
   * Handles the client ComboBox selection changes. When the selected client changes,
   * the project ComboBox options should change to display projects associated with the
   * selected client.
   * @param option ComboBoxOption of the selected client. Else, null if an option is not selected.
   */
  async function onClientChange(option: ComboBoxOption | null) {
    await Promise.resolve(setProject(undefined));
    setClient(option ?? undefined);

    if (option !== null) {
      setClientError('');
    }
  }

  /**
   * Handles the project ComboBox selection changes.
   * @param option ComboBoxOption of the selected project. Else, null if an option is not selected.
   */
  function onProjectChange(option: ComboBoxOption | null) {
    setProject(option ?? undefined);

    if (option !== null) {
      setProjectError('');
    }
  }

  /**
   * Gets the ComboBoxOptions of all projects associated with the selected client
   * @param clientId client Id
   * @returns ComboBoxOption[]
   */
  function getProjectComboBoxOptions(clientId: number) {
    if (!clients) return [];

    const client = clients?.find(c => c.id === clientId);
    const options = getComboBoxOptions(client?.projects);
    return options;
  }

  /**
   * Gets the initial option for the Client ComboBox. If a priority is being created, this should be
   * undefined. If a priority is being edited, this should be based on the client of the existing priority.
   * @returns ComboBoxOption for client if a priority is being edited. Else, undefined if a new priority is being created.
   */
  function getInitialClientComboBoxOption() {
    if (!priority?.project) return;

    const option = {
      label: priority.project.clientName,
      value: priority.project.clientID,
    } as ComboBoxOption;

    return option;
  }

  /**
   * Gets the initial option for the Project ComboBox. If a priority is being created, this should be
   * undefined. If a priority is being edited, this should be based on the project of the existing priority.
   * @returns ComboBoxOption for project if a priority is being edited. Else, undefined if a new priority is being created.
   */
  function getInitialProjectComboBoxOption() {
    if (!priority || !priority.project) return;

    const option = {
      label: priority.project.name,
      value: priority.projectId,
    } as ComboBoxOption;

    return option;
  }

  /**
   * Validates client and project input fields
   * @returns True if client and project fields are not empty. Else, returns false.
   */
  const validate = () => {
    const errorClient = client ? '' : 'Please select a client';
    const errorProject = project ? '' : 'Please select a project';
    setClientError(errorClient);
    setProjectError(errorProject);
    return project && client;
  };

  /**
   * Form submission handler
   * @param data form input field data
   */
  const submit: SubmitHandler<IPriorityFormFields> = data => {
    if (validate()) {
      onSubmit({
        id: data.id,
        employeeID: data.employee?.value ?? priority?.employeeID,
        projectId: project?.value,
        order: data.order,
        status: data.status,
        utilisation: data.utilisation,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
      } as PriorityItemDto);
    }
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Grid container spacing={1.5} width={'35rem'}>
            <Grid item xs={12}>
              <ComboBox
                name={'client'}
                label={'Client'}
                options={getComboBoxOptions(clients)}
                onChange={onClientChange}
                value={client}
                error={clientError}
                creatable
                onCreatableClick={nameSuggestion => {
                  setClientNameSuggestion(nameSuggestion);
                  setIsCreateClientFormOpen(true);
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <ComboBox
                name={'project'}
                label={'Project'}
                options={client ? getProjectComboBoxOptions(client.value) : []}
                onChange={onProjectChange}
                value={project}
                error={projectError}
                creatable={Boolean(client)}
                onCreatableClick={nameSuggestion => {
                  setProjectNameSuggestion(nameSuggestion);
                  setIsCreateProjectFormOpen(true);
                }}
              />
            </Grid>

            {showEmployeeDropdown && (
              <Grid item xs={12}>
                <SelectionBox name={'employee'} label={'Employee'} options={getComboBoxOptions(employees)} required />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField name={'description'} label={'Description'} multiline />
            </Grid>

            <Grid item xs={12}>
              <TextField name={'utilisation'} label={'Utilisation (%)'} numericInput min={0} max={100} required />
            </Grid>

            <Grid item container direction="row" spacing={1}>
              <Grid item xs={6}>
                <CalendarDatePicker name={'startDate'} label={'Start Date'} proxy={false} />
              </Grid>
              <Grid item xs={6}>
                <CalendarDatePicker name={'endDate'} label={'End Date'} minDate={watch('startDate')} proxy={false} />
              </Grid>
            </Grid>

            <Grid container item xs={12} justifyContent={'center'} marginBottom={1.5}>
              <RadioButtonGroup name={'status'} label={'Status'}>
                <RadioButton value={Status.Request} label={Status.Request} />
                <RadioButton value={Status.LeadOnly} label={Status.LeadOnly} />
                <RadioButton value={Status.InProgress} label={Status.InProgress} />
                <RadioButton value={Status.OnHold} label={Status.OnHold} />
                <RadioButton value={Status.Complete} label={Status.Complete} />
              </RadioButtonGroup>
            </Grid>

            {priority && (
              <Grid item xs={12}>
                <PriorityHistory priority={priority} />
              </Grid>
            )}

            <Grid item container direction="row" justifyContent="flex-end" spacing={1}>
              <Grid item>
                <Button
                  variant="contained"
                  size="large"
                  className={classes.submitButton}
                  type="submit"
                  onClick={validate}
                >
                  {!priority ? 'Create' : 'Update'}
                </Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" color="neutral" size="large" onClick={onCancel}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>

      <CreateClientPopup
        isOpen={isCreateClientFormOpen}
        setIsOpen={setIsCreateClientFormOpen}
        onSuccess={(newClient: ClientDto) => {
          const option = getComboBoxOption(newClient);
          setClient(option);
        }}
        clientNameSuggestion={clientNameSuggestion}
      />

      <CreateProjectPopup
        isOpen={isCreateProjectFormOpen}
        setIsOpen={setIsCreateProjectFormOpen}
        client={client && convertToClientDto(client)}
        onSuccess={(newProject: ProjectDto) => {
          const option = getComboBoxOption(newProject);
          setProject(option);
        }}
        projectNameSuggestion={projectNameSuggestion}
      />
    </div>
  );
}

export default PriorityForm;
